import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Group } from '../containers/Group/Group.component';
import { useStepVisibilityChange } from './hooks/step-visibility-change.hook';
import { StepperMenu } from './StepperMenu/StepperMenu';
import { isStepChild } from './utils/is-step-child';
import { StepHandle } from './Step.component';

export interface IStepContainerProps {
  children: React.ReactNode;
  showMenuStepper?: boolean;
  preStepperComponent?: React.ReactNode;
}

export const StepContainer: FC<IStepContainerProps> = ({ children, showMenuStepper, preStepperComponent }) => {
  const stepsRef = useRef<(StepHandle | null)[]>([]);
  const [firstVisibleStep, setFirstVisibleStep] = useState(0);

  const stepChildren = useMemo(() => React.Children.toArray(children).filter(isStepChild), [children]);
  const menuStepTitles = useMemo(
    () => stepChildren.map(child => child.props.menuTitle || child.props.title),
    [stepChildren],
  );

  const elementVisibilityChanged = useStepVisibilityChange(setFirstVisibleStep);

  const jumpToStep = useCallback((index: number) => {
    if (stepsRef.current?.[index]) {
      stepsRef.current[index]?.jumpToStep();
    }
  }, []);

  return (
    <Group width={'100%'} gap={10}>
      {showMenuStepper && (
        <StepperMenu onStepClicked={jumpToStep} steps={menuStepTitles} currentStep={firstVisibleStep} />
      )}
      <Group vertical gap={0} width={'100%'}>
        {preStepperComponent}
        {stepChildren.map((child, i) => {
          return React.cloneElement(child, {
            currentStep: `${i + 1}`,
            subject: `step-${i + 1}`,
            ref: c => {
              stepsRef.current[i] = c;
            },
            onVisibilityChanged: (isVisible: boolean) => {
              elementVisibilityChanged(i, isVisible);
            },
          });
        })}
      </Group>
    </Group>
  );
};
